import React, { useEffect, useMemo, useState } from "react";
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, BanknotesIcon, CalendarDaysIcon, FolderOpenIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from "chart.js";
import EmptyState from "../components/EmptyState";
import LoadingScreen from "../components/LoadingScreen";
import SectionCard from "../components/SectionCard";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { subscribeByOwner } from "../services/db";
import { formatDateTime, formatMoneyWithUnit, toEnglishDigits } from "../utils/format";
import { buildDailyChartData, calculateProjectProgress, calculateProjectsOverview, filterTransactionsByRange, getRanges, sortByDateDesc, sumTransactions } from "../utils/report";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function DashboardPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribers = [
      subscribeByOwner("transactions", user.uid, setTransactions),
      subscribeByOwner("appointments", user.uid, setAppointments),
      subscribeByOwner("projects", user.uid, setProjects),
      subscribeByOwner("projectTasks", user.uid, setTasks)
    ];
    const timer = setTimeout(() => setReady(true), 300);
    return () => {
      unsubscribers.forEach((unsub) => unsub?.());
      clearTimeout(timer);
    };
  }, [user?.uid]);

  const ranges = useMemo(() => getRanges(), []);
  const todaySummary = useMemo(() => sumTransactions(filterTransactionsByRange(transactions, ranges.today.start, ranges.today.end)), [transactions, ranges.today.end, ranges.today.start]);
  const weekSummary = useMemo(() => sumTransactions(filterTransactionsByRange(transactions, ranges.week.start, ranges.week.end)), [transactions, ranges.week.end, ranges.week.start]);
  const monthSummary = useMemo(() => sumTransactions(filterTransactionsByRange(transactions, ranges.month.start, ranges.month.end)), [transactions, ranges.month.end, ranges.month.start]);
  const projectsOverview = useMemo(() => calculateProjectsOverview(projects), [projects]);
  const recentTransactions = useMemo(() => sortByDateDesc(transactions).slice(0, 5), [transactions]);
  const upcomingAppointments = useMemo(() => [...appointments].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)).slice(0, 5), [appointments]);
  const chartData = useMemo(() => buildDailyChartData(transactions), [transactions]);

  const barData = useMemo(() => ({
    labels: chartData.labels,
    datasets: [
      { label: "الدخل", data: chartData.income, backgroundColor: "rgba(42, 104, 65, 0.85)", borderRadius: 8 },
      { label: "المصاريف", data: chartData.expense, backgroundColor: "rgba(185, 28, 28, 0.85)", borderRadius: 8 }
    ]
  }), [chartData]);

  const doughnutData = useMemo(() => ({
    labels: ["المستلم", "المتبقي"],
    datasets: [{ data: [projectsOverview.received, projectsOverview.remaining || 1], backgroundColor: ["#2A6841", "#EA962D"], borderWidth: 0 }]
  }), [projectsOverview.received, projectsOverview.remaining]);

  const topProjects = useMemo(() => projects.map((project) => ({ ...project, progress: calculateProjectProgress(tasks.filter((task) => task.projectId === project.id)) })).sort((a, b) => b.totalValue - a.totalValue).slice(0, 3), [projects, tasks]);

  if (!ready) return <LoadingScreen text="جارٍ تجهيز لوحة التحكم..." />;

  return (
    <div className="page-shell space-y-4">
      <section className="mini-grid">
        <StatCard title="صافي اليوم" value={formatMoneyWithUnit(todaySummary.net)} subtitle={`دخل ${formatMoneyWithUnit(todaySummary.income)}`} icon={BanknotesIcon} tone="brand" />
        <StatCard title="دخل الأسبوع" value={formatMoneyWithUnit(weekSummary.income)} subtitle={`صافي ${formatMoneyWithUnit(weekSummary.net)}`} icon={ArrowTrendingUpIcon} tone="success" />
        <StatCard title="مصروف الشهر" value={formatMoneyWithUnit(monthSummary.expense)} subtitle={`صافي ${formatMoneyWithUnit(monthSummary.net)}`} icon={ArrowTrendingDownIcon} tone="danger" />
        <StatCard title="متبقي المشاريع" value={formatMoneyWithUnit(projectsOverview.remaining)} subtitle={`${toEnglishDigits(projectsOverview.active)} نشط`} icon={FolderOpenIcon} tone="accent" />
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.25fr_0.75fr]">
        <SectionCard title="حركة آخر 7 أيام" subtitle="مقارنة الدخل والمصاريف بشكل يومي">
          <div className="chart-box"><Bar data={barData} options={{ maintainAspectRatio: false, responsive: true, plugins: { legend: { position: "bottom" } } }} /></div>
        </SectionCard>
        <SectionCard title="تحصيل المشاريع" subtitle="المستلم مقابل المتبقي">
          <div className="chart-box mx-auto max-w-[340px]"><Doughnut data={doughnutData} options={{ maintainAspectRatio: false, responsive: true, plugins: { legend: { position: "bottom" } } }} /></div>
        </SectionCard>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionCard title="آخر الحركات المالية" subtitle="أحدث الدخل والمصاريف المسجلة" action={<Link to="/transactions" className="rounded-full bg-brand-50 px-4 py-2 text-sm font-bold text-brand-700">عرض الكل</Link>}>
          {!recentTransactions.length ? (
            <EmptyState title="لا توجد حركات مالية بعد" description="أضف أول دخل أو مصروف ليظهر هنا مباشرة." />
          ) : (
            <div className="space-y-3">
              {recentTransactions.map((item) => (
                <div key={item.id} className="rounded-2xl border border-slate-200 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-extrabold text-slate-800 sm:text-base">{item.title}</div>
                      <div className="mt-1 text-[11px] text-slate-500 sm:text-xs">{item.category || "بدون تصنيف"} • {item.date || "—"}</div>
                    </div>
                    <div className={`shrink-0 rounded-full px-3 py-1 text-xs font-extrabold sm:text-sm ${item.type === "income" ? "bg-emerald-50 text-income" : "bg-rose-50 text-expense"}`}>{item.type === "income" ? "+" : "-"}{formatMoneyWithUnit(item.amount)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="المواعيد القادمة" subtitle="أقرب المواعيد المسجلة" action={<Link to="/appointments" className="rounded-full bg-brand-50 px-4 py-2 text-sm font-bold text-brand-700">فتح الدفتر</Link>}>
          {!upcomingAppointments.length ? (
            <EmptyState title="لا توجد مواعيد حالياً" description="أضف موعداً جديداً ليظهر في هذه القائمة." />
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((item) => (
                <div key={item.id} className="rounded-2xl border border-slate-200 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-extrabold text-slate-800 sm:text-base">{item.title}</div>
                      <div className="mt-1 text-[11px] text-slate-500 sm:text-xs">{item.projectName || "موعد عام"}</div>
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-700 sm:text-xs"><CalendarDaysIcon className="h-4 w-4" />{formatDateTime(item.date, item.time)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </section>

      <SectionCard title="أهم المشاريع" subtitle="متابعة سريعة للمشاريع الحالية" action={<Link to="/projects" className="rounded-full bg-brand-50 px-4 py-2 text-sm font-bold text-brand-700">إدارة المشاريع</Link>}>
        {!topProjects.length ? (
          <EmptyState title="لا توجد مشاريع بعد" description="أضف مشروعك الأول مع المبلغ الكلي والدفعات والمهام." />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {topProjects.map((project) => (
              <Link key={project.id} to={`/projects/${project.id}`} className="mobile-card transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-base font-extrabold text-brand-800 sm:text-lg">{project.title}</div>
                    <div className="text-sm text-slate-500">{project.clientName || "بدون عميل"}</div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${project.status === "completed" ? "bg-emerald-50 text-income" : project.status === "on-hold" ? "bg-amber-50 text-amber-700" : "bg-brand-50 text-brand-700"}`}>{project.status === "completed" ? "مكتمل" : project.status === "on-hold" ? "معلق" : "نشط"}</span>
                </div>
                <div className="mt-4 space-y-2 text-sm"><div className="flex items-center justify-between"><span className="text-slate-500">قيمة المشروع</span><strong>{formatMoneyWithUnit(project.totalValue)}</strong></div><div className="flex items-center justify-between"><span className="text-slate-500">المتبقي</span><strong>{formatMoneyWithUnit(project.remainingAmount)}</strong></div></div>
                <div className="mt-4"><div className="mb-2 flex items-center justify-between text-[11px] font-extrabold text-slate-500"><span>نسبة الإنجاز</span><span>{toEnglishDigits(project.progress)}%</span></div><div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-gradient-to-r from-brand-700 to-accent-500" style={{ width: `${project.progress}%` }} /></div></div>
              </Link>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
